import {
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
  query,
  orderBy,
  writeBatch,
  setDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { getEffectiveTenantId, tenantCollection, tenantDoc, tenantPatientCollection, tenantPatientDoc } from './tenantContext';
import { createPatientEncounter, authorFieldsFromProfile } from './patientEncounterService';
import { resolveSystemEncounterSessionMinutes } from './sessionMinutesDefaultsService';
import { resolveProviderChangeDischargeReason } from './programReasonsService';
import { logPatientAuditUpdate } from './patientAuditService';
import { todayYmdInOrgTimeZone } from '../utils/organizationalTime';
import { recordDailyWin } from '../utils/dailyWins';

const PROGRAMS_COL = 'patientPrograms';
const AUDIT_COL = 'patientProgramAuditLog';

export const PROGRAM_STATUS = {
  OUTREACH: 'outreach',
  PRE_SERVICE: 'preService',
  ACTIVE: 'active',
  ON_HOLD: 'onHold',
  DISCHARGED: 'discharged',
  /** Terminal without ever enrolling — closed from Outreach or Pre-Service. Distinct from Discharged. */
  CLOSED: 'closed',
};

export const PROGRAM_STATUS_LABELS = {
  [PROGRAM_STATUS.OUTREACH]: 'Outreach',
  [PROGRAM_STATUS.PRE_SERVICE]: 'Pre-Service',
  [PROGRAM_STATUS.ACTIVE]: 'Active',
  [PROGRAM_STATUS.ON_HOLD]: 'On Hold',
  [PROGRAM_STATUS.DISCHARGED]: 'Discharged',
  [PROGRAM_STATUS.CLOSED]: 'Closed',
};

/** Labels for the stage a program was in when it was closed (session.closedFromStatus). */
export const CLOSED_FROM_LABELS = {
  [PROGRAM_STATUS.OUTREACH]: 'Outreach',
  [PROGRAM_STATUS.PRE_SERVICE]: 'Pre-Service',
};

/** Solid status colors (badge dots, modal status badge). */
export const PROGRAM_STATUS_COLORS = {
  [PROGRAM_STATUS.OUTREACH]: '#999',
  [PROGRAM_STATUS.PRE_SERVICE]: '#0277bd',
  [PROGRAM_STATUS.ACTIVE]: '#2e7d32',
  [PROGRAM_STATUS.ON_HOLD]: '#e65100',
  [PROGRAM_STATUS.DISCHARGED]: '#c62828',
  [PROGRAM_STATUS.CLOSED]: '#546e7a',
};

/**
 * Tinted chip colors (pale background + darker same-hue text) for the
 * attention statuses surfaced in the patient header callout. All pairs meet
 * WCAG AA 4.5:1 contrast, unlike the solid values used as bare text.
 */
export const PROGRAM_STATUS_CHIP_COLORS = {
  [PROGRAM_STATUS.OUTREACH]: { bg: 'var(--tk-bg-f1f3f4)', text: 'var(--tk-text-5f6368)' },
  [PROGRAM_STATUS.PRE_SERVICE]: { bg: 'var(--tk-bg-e3f2fd)', text: 'var(--tk-text-01579b)' },
  [PROGRAM_STATUS.ON_HOLD]: { bg: 'var(--tk-bg-fff3e0)', text: 'var(--tk-text-b34700)' },
};

const VALID_PROGRAM_STATUSES = new Set(Object.values(PROGRAM_STATUS));

/**
 * Returns programs that block de-activating the patient (client-side only).
 *
 * Blocking: any row with status `active`, `onHold`, or `preService` (a scheduled
 * setup must be resolved explicitly, not silently orphaned).
 * Missing/invalid status: treated as blocking with separate messaging in the UI.
 *
 * **Server enforcement:** Firestore rules cannot cheaply read all patientPrograms on a patient
 * update; bypass is possible via the SDK. Prefer a denormalized patient flag maintained by
 * triggers, or a callable that deactivates after server-side checks.
 *
 * @param {{ id?: string, status?: string, serviceTypeAbbreviation?: string }[]|null|undefined} programs
 * @returns {{ blocking: object[], unknownRows: object[] }} blocking = active/onHold; unknownRows = bad/missing status
 */
export function getProgramsBlockingPatientDeactivation(programs) {
  const blocking = [];
  const unknownRows = [];
  if (!Array.isArray(programs)) return { blocking, unknownRows };

  for (const p of programs) {
    const status = p?.status;
    if (status == null || status === '' || !VALID_PROGRAM_STATUSES.has(status)) {
      unknownRows.push(p);
      continue;
    }
    if (
      status === PROGRAM_STATUS.ACTIVE
      || status === PROGRAM_STATUS.ON_HOLD
      || status === PROGRAM_STATUS.PRE_SERVICE
    ) {
      blocking.push(p);
    }
  }
  return { blocking, unknownRows };
}

function todayYmd() {
  return new Date().toLocaleDateString('en-CA');
}

async function writeAudit(tenantId, { patientId, programDocId, serviceTypeAbbreviation, action, changes, actorUid, actorEmail }) {
  await addDoc(tenantCollection(tenantId, AUDIT_COL), {
    patientId,
    programDocId: programDocId || null,
    serviceTypeAbbreviation: serviceTypeAbbreviation || null,
    action,
    changes: changes || null,
    actorUid: actorUid || null,
    actorEmail: actorEmail || null,
    createdAt: serverTimestamp(),
  });
}

/**
 * Program-transition encounter (discharge/close/enroll/hold notes). Dated to
 * the transition's effective date — not "today" — so the minutes land in the
 * month the status actually changed; future dates are clamped to org-today so
 * a scheduled transition can't push minutes into an unstarted month. These
 * encounters are exempt from out-of-service minute suppression.
 */
async function createProgramEncounter(tenantId, patientId, bodyHtml, actorContext, transitionEffectiveDate = null) {
  try {
    const profile = actorContext?.profile || {
      id: actorContext?.uid,
      firstName: actorContext?.firstName || '',
      lastName: actorContext?.lastName || '',
      displayName: actorContext?.displayName || '',
    };
    const orgToday = todayYmdInOrgTimeZone();
    const effectiveDate =
      transitionEffectiveDate && /^\d{4}-\d{2}-\d{2}$/.test(transitionEffectiveDate) && transitionEffectiveDate <= orgToday
        ? transitionEffectiveDate
        : orgToday;
    const sessionMinutes = await resolveSystemEncounterSessionMinutes('program_status_change');
    await createPatientEncounter({
      patientId,
      bodyHtml,
      effectiveDate,
      encounterType: 'program_status_change',
      profile,
      isSystemGenerated: true,
      systemReason: 'program_status_change',
      sessionMinutes,
    });
  } catch (e) {
    console.warn('Program encounter log failed', e);
  }
}

const SETUP_CPT_CODES = ['99453', 'A9901'];

/**
 * Plant an authoritative "setup billed in prior system" CPT entry in the
 * enrollment-month billingData doc. The CPT engine preserves entries flagged
 * `externallyBilled` and counts them as prior-billed for the whole session,
 * so the Setup column reads "Billed" instead of accruing a new setup charge.
 * Best-effort: enrollment must not fail because of this write.
 */
async function markSetupExternallyBilled(tenantId, patientId, enrollmentDate, sessionNumber) {
  try {
    const ym = String(enrollmentDate).slice(0, 7);
    if (!/^\d{4}-\d{2}$/.test(ym)) return;
    const ref = tenantPatientDoc(tenantId, patientId, 'billingData', ym);
    await setDoc(ref, {
      cptEvaluation: Object.fromEntries(
        SETUP_CPT_CODES.map((c) => [c, { status: 'met', quantity: 1, externallyBilled: true }]),
      ),
      programEnrollmentDate: enrollmentDate,
      programSessionNumber: sessionNumber || 1,
    }, { merge: true });
  } catch (e) {
    console.warn('Failed to mark setup as externally billed', e);
  }
}

export async function getPatientPrograms(patientId) {
  const tenantId = await getEffectiveTenantId();
  if (!tenantId || !patientId) return [];
  const col = tenantPatientCollection(tenantId, patientId, PROGRAMS_COL);
  const snap = await getDocs(query(col, orderBy('createdAt', 'asc')));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export function subscribeToPatientPrograms(patientId, callback) {
  let unsubscribe = () => {};
  (async () => {
    const tenantId = await getEffectiveTenantId();
    if (!tenantId || !patientId) {
      callback([]);
      return;
    }
    const col = tenantPatientCollection(tenantId, patientId, PROGRAMS_COL);
    unsubscribe = onSnapshot(query(col, orderBy('createdAt', 'asc')), (snap) => {
      callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, () => callback([]));
  })();
  return () => unsubscribe();
}

/**
 * Bulk-add programs when a new patient is created.
 * @param {string} patientId
 * @param {{ id: string, serviceTypeAbbreviation: string, serviceTypeName: string }[]} serviceTypes
 * @param {{ uid: string, email?: string }} actorContext
 */
export async function addPatientPrograms(patientId, serviceTypes, actorContext) {
  const tenantId = await getEffectiveTenantId();
  if (!tenantId || !patientId || !serviceTypes?.length) return;
  for (const st of serviceTypes) {
    const col = tenantPatientCollection(tenantId, patientId, PROGRAMS_COL);
    const payload = {
      tenantId,
      serviceTypeId: st.id,
      serviceTypeAbbreviation: st.serviceTypeAbbreviation,
      serviceTypeName: st.serviceTypeName || '',
      status: PROGRAM_STATUS.OUTREACH,
      sessions: [{ sessionNumber: 1, orderDate: null, scheduledEnrollmentDate: null, enrollmentDate: null, dischargeDate: null, dischargeReasonId: null, dischargeReasonName: null }],
      holdReasonId: null,
      holdReasonName: null,
      holdUntilDate: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: actorContext?.uid || null,
      updatedBy: actorContext?.uid || null,
    };
    const ref = await addDoc(col, payload);
    await writeAudit(tenantId, {
      patientId,
      programDocId: ref.id,
      serviceTypeAbbreviation: st.serviceTypeAbbreviation,
      action: 'program_added',
      actorUid: actorContext?.uid,
      actorEmail: actorContext?.email,
    });
  }
}

export async function setOrderDate(patientId, programDocId, orderDate, actorContext) {
  const tenantId = await getEffectiveTenantId();
  if (!tenantId || !patientId || !programDocId) throw new Error('Missing context');
  const ref = tenantPatientDoc(tenantId, patientId, PROGRAMS_COL, programDocId);
  const { getDoc } = await import('firebase/firestore');
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Program not found');
  const data = snap.data();
  const sessions = [...(data.sessions || [])];
  const currentIdx = sessions.length - 1;
  if (currentIdx < 0) throw new Error('No session found');
  const oldOrderDate = sessions[currentIdx]?.orderDate ?? null;
  sessions[currentIdx] = { ...sessions[currentIdx], orderDate };
  await updateDoc(ref, {
    tenantId,
    sessions,
    updatedAt: serverTimestamp(),
    updatedBy: actorContext?.uid || null,
  });
  await writeAudit(tenantId, {
    patientId,
    programDocId,
    serviceTypeAbbreviation: data.serviceTypeAbbreviation,
    action: 'order_date_set',
    changes: { orderDate: { old: oldOrderDate, new: orderDate } },
    actorUid: actorContext?.uid,
    actorEmail: actorContext?.email,
  });
}

/**
 * Record a *planned* enrollment date and move the program to Pre-Service.
 * The session's `enrollmentDate` stays null — billing and census anchor only
 * to the actual date written by `activateProgram`. Future dates are allowed
 * here (this is the whole point of Pre-Service).
 */
export async function setScheduledEnrollmentDate(patientId, programDocId, scheduledEnrollmentDate, actorContext, orderDate) {
  const tenantId = await getEffectiveTenantId();
  if (!tenantId || !patientId || !programDocId) throw new Error('Missing context');
  if (!scheduledEnrollmentDate) throw new Error('Scheduled enrollment date is required');
  const ref = tenantPatientDoc(tenantId, patientId, PROGRAMS_COL, programDocId);
  const { getDoc } = await import('firebase/firestore');
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Program not found');
  const data = snap.data();
  const sessions = [...(data.sessions || [])];
  const currentIdx = sessions.length - 1;
  if (currentIdx < 0) throw new Error('No session found');
  const oldScheduled = sessions[currentIdx]?.scheduledEnrollmentDate ?? null;
  const sessionPatch = { ...sessions[currentIdx], scheduledEnrollmentDate };
  if (orderDate) sessionPatch.orderDate = orderDate;
  sessions[currentIdx] = sessionPatch;
  await updateDoc(ref, {
    tenantId,
    status: PROGRAM_STATUS.PRE_SERVICE,
    sessions,
    updatedAt: serverTimestamp(),
    updatedBy: actorContext?.uid || null,
  });
  await writeAudit(tenantId, {
    patientId,
    programDocId,
    serviceTypeAbbreviation: data.serviceTypeAbbreviation,
    action: 'pre_service_scheduled',
    changes: {
      status: { old: data.status, new: PROGRAM_STATUS.PRE_SERVICE },
      scheduledEnrollmentDate: { old: oldScheduled, new: scheduledEnrollmentDate },
    },
    actorUid: actorContext?.uid,
    actorEmail: actorContext?.email,
  });
  const abbr = data.serviceTypeAbbreviation || '?';
  const sessNum = sessions[currentIdx].sessionNumber || 1;
  if (data.status !== PROGRAM_STATUS.PRE_SERVICE) {
    await createProgramEncounter(tenantId, patientId,
      `Patient scheduled for <b>${abbr}</b> program enrollment on ${scheduledEnrollmentDate} (Session ${sessNum}, Pre-Service).`,
      actorContext);
  }
}

/**
 * Activate the program: write the *actual* enrollment date and set status
 * Active. Callable from Outreach (same-day enrollment) or Pre-Service.
 * This date anchors billing session scoping and census reports, so it must
 * not be in the future.
 */
export async function activateProgram(patientId, programDocId, enrollmentDate, actorContext, setupAlreadyBilled = false) {
  const tenantId = await getEffectiveTenantId();
  if (!tenantId || !patientId || !programDocId) throw new Error('Missing context');
  if (!enrollmentDate) throw new Error('Enrollment date is required');
  if (enrollmentDate > todayYmd()) throw new Error('Enrollment date cannot be in the future. Use Schedule Enrollment for planned dates.');
  const ref = tenantPatientDoc(tenantId, patientId, PROGRAMS_COL, programDocId);
  const { getDoc } = await import('firebase/firestore');
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Program not found');
  const data = snap.data();
  const sessions = [...(data.sessions || [])];
  const currentIdx = sessions.length - 1;
  if (currentIdx < 0) throw new Error('No session found');
  sessions[currentIdx] = {
    ...sessions[currentIdx],
    enrollmentDate,
    ...(setupAlreadyBilled
      ? { setupExternallyBilled: true, setupExternallyBilledSource: 'manual' }
      : {}),
  };
  await updateDoc(ref, {
    tenantId,
    status: PROGRAM_STATUS.ACTIVE,
    sessions,
    updatedAt: serverTimestamp(),
    updatedBy: actorContext?.uid || null,
  });
  await writeAudit(tenantId, {
    patientId,
    programDocId,
    serviceTypeAbbreviation: data.serviceTypeAbbreviation,
    action: 'enrollment',
    changes: { status: { old: data.status, new: PROGRAM_STATUS.ACTIVE }, enrollmentDate: { old: null, new: enrollmentDate } },
    actorUid: actorContext?.uid,
    actorEmail: actorContext?.email,
  });
  recordDailyWin({
    uid: actorContext?.uid,
    type: 'patientActivated',
    patientId,
    eventId: `program-active:${programDocId}:${enrollmentDate}`,
  });
  const abbr = data.serviceTypeAbbreviation || '?';
  const sessNum = sessions[currentIdx].sessionNumber || 1;
  if (setupAlreadyBilled) {
    await markSetupExternallyBilled(tenantId, patientId, enrollmentDate, sessNum);
  }
  await createProgramEncounter(tenantId, patientId,
    `Patient enrolled into <b>${abbr}</b> program as Active (Session ${sessNum}, effective ${enrollmentDate}).${setupAlreadyBilled ? ' Setup billing marked as already completed in prior system.' : ''}`,
    actorContext, enrollmentDate);
}

/** Cancel a Pre-Service schedule and return the program to Outreach. */
export async function cancelPreService(patientId, programDocId, actorContext) {
  const tenantId = await getEffectiveTenantId();
  if (!tenantId || !patientId || !programDocId) throw new Error('Missing context');
  const ref = tenantPatientDoc(tenantId, patientId, PROGRAMS_COL, programDocId);
  const { getDoc } = await import('firebase/firestore');
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Program not found');
  const data = snap.data();
  if (data.status !== PROGRAM_STATUS.PRE_SERVICE) throw new Error('Program is not in Pre-Service');
  const sessions = [...(data.sessions || [])];
  const currentIdx = sessions.length - 1;
  if (currentIdx < 0) throw new Error('No session found');
  const oldScheduled = sessions[currentIdx]?.scheduledEnrollmentDate ?? null;
  sessions[currentIdx] = { ...sessions[currentIdx], scheduledEnrollmentDate: null };
  await updateDoc(ref, {
    tenantId,
    status: PROGRAM_STATUS.OUTREACH,
    sessions,
    updatedAt: serverTimestamp(),
    updatedBy: actorContext?.uid || null,
  });
  await writeAudit(tenantId, {
    patientId,
    programDocId,
    serviceTypeAbbreviation: data.serviceTypeAbbreviation,
    action: 'pre_service_cancelled',
    changes: {
      status: { old: PROGRAM_STATUS.PRE_SERVICE, new: PROGRAM_STATUS.OUTREACH },
      scheduledEnrollmentDate: { old: oldScheduled, new: null },
    },
    actorUid: actorContext?.uid,
    actorEmail: actorContext?.email,
  });
  const abbr = data.serviceTypeAbbreviation || '?';
  await createProgramEncounter(tenantId, patientId,
    `Patient's <b>${abbr}</b> program returned to Outreach \u2014 scheduled enrollment cancelled.`,
    actorContext);
}

/**
 * Close a program from Outreach or Pre-Service — the patient never enrolled,
 * so this is NOT a discharge. The session's `enrollmentDate` stays null
 * (billing/census/CPT remain unaffected) and `closedFromStatus` records which
 * stage the program was in when closed. `scheduledEnrollmentDate` is kept for
 * history.
 *
 * @param {string} patientId
 * @param {string} programDocId
 * @param {{ closedDate: string, reasonId?: string|null, reasonName?: string|null, note?: string|null }} opts
 * @param {{ uid?: string, email?: string, profile?: object }} actorContext
 */
export async function closeProgram(patientId, programDocId, opts, actorContext) {
  const { closedDate, reasonId = null, reasonName = null, note = null } = opts || {};
  const tenantId = await getEffectiveTenantId();
  if (!tenantId || !patientId || !programDocId) throw new Error('Missing context');
  if (!closedDate) throw new Error('Close date is required');
  if (closedDate > todayYmd()) throw new Error('Close date cannot be in the future.');
  const ref = tenantPatientDoc(tenantId, patientId, PROGRAMS_COL, programDocId);
  const { getDoc } = await import('firebase/firestore');
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Program not found');
  const data = snap.data();
  if (data.status !== PROGRAM_STATUS.OUTREACH && data.status !== PROGRAM_STATUS.PRE_SERVICE) {
    throw new Error('Only Outreach or Pre-Service programs can be closed. Use Discharge for enrolled programs.');
  }
  const sessions = [...(data.sessions || [])];
  const currentIdx = sessions.length - 1;
  if (currentIdx < 0) throw new Error('No session found');
  if (sessions[currentIdx]?.enrollmentDate) {
    throw new Error('This session already has an enrollment date and must be discharged instead.');
  }
  const closedFromStatus = data.status;
  sessions[currentIdx] = {
    ...sessions[currentIdx],
    closedDate,
    closedFromStatus,
    closedReasonId: reasonId || null,
    closedReasonName: reasonName || null,
    closedNote: note || null,
  };
  await updateDoc(ref, {
    tenantId,
    status: PROGRAM_STATUS.CLOSED,
    sessions,
    updatedAt: serverTimestamp(),
    updatedBy: actorContext?.uid || null,
  });
  await writeAudit(tenantId, {
    patientId,
    programDocId,
    serviceTypeAbbreviation: data.serviceTypeAbbreviation,
    action: 'program_closed',
    changes: {
      status: { old: closedFromStatus, new: PROGRAM_STATUS.CLOSED },
      closedDate: { old: null, new: closedDate },
      closedReasonName: { old: null, new: reasonName },
    },
    actorUid: actorContext?.uid,
    actorEmail: actorContext?.email,
  });
  const abbr = data.serviceTypeAbbreviation || '?';
  const stageLabel = CLOSED_FROM_LABELS[closedFromStatus] || closedFromStatus;
  const reasonPart = reasonName ? ` Reason: ${reasonName}.` : '';
  await createProgramEncounter(tenantId, patientId,
    `Patient's <b>${abbr}</b> program was closed prior to enrollment (removed from ${stageLabel}, effective ${closedDate}).${reasonPart}`,
    actorContext, closedDate);
}

/**
 * Reopen a Closed program: append a fresh session and return to Outreach.
 * The closed session stays in history (mirrors Re-Enroll from Discharged).
 */
export async function reopenProgram(patientId, programDocId, actorContext) {
  const tenantId = await getEffectiveTenantId();
  if (!tenantId || !patientId || !programDocId) throw new Error('Missing context');
  const ref = tenantPatientDoc(tenantId, patientId, PROGRAMS_COL, programDocId);
  const { getDoc } = await import('firebase/firestore');
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Program not found');
  const data = snap.data();
  if (data.status !== PROGRAM_STATUS.CLOSED) throw new Error('Only closed programs can be reopened');
  const sessions = [...(data.sessions || [])];
  const nextNum = sessions.length > 0 ? (sessions[sessions.length - 1].sessionNumber || sessions.length) + 1 : 1;
  sessions.push({
    sessionNumber: nextNum,
    orderDate: null,
    scheduledEnrollmentDate: null,
    enrollmentDate: null,
    dischargeDate: null,
    dischargeReasonId: null,
    dischargeReasonName: null,
  });
  await updateDoc(ref, {
    tenantId,
    status: PROGRAM_STATUS.OUTREACH,
    sessions,
    updatedAt: serverTimestamp(),
    updatedBy: actorContext?.uid || null,
  });
  await writeAudit(tenantId, {
    patientId,
    programDocId,
    serviceTypeAbbreviation: data.serviceTypeAbbreviation,
    action: 'program_reopened',
    changes: {
      status: { old: PROGRAM_STATUS.CLOSED, new: PROGRAM_STATUS.OUTREACH },
      sessionNumber: nextNum,
    },
    actorUid: actorContext?.uid,
    actorEmail: actorContext?.email,
  });
  const abbr = data.serviceTypeAbbreviation || '?';
  await createProgramEncounter(tenantId, patientId,
    `Patient's <b>${abbr}</b> program reopened \u2014 returned to Outreach (Session ${nextNum}).`,
    actorContext);
}

export async function setDischargeDate(patientId, programDocId, dischargeDate, dischargeReasonId, dischargeReasonName, actorContext) {
  const tenantId = await getEffectiveTenantId();
  if (!tenantId || !patientId || !programDocId) throw new Error('Missing context');
  const ref = tenantPatientDoc(tenantId, patientId, PROGRAMS_COL, programDocId);
  const { getDoc } = await import('firebase/firestore');
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Program not found');
  const data = snap.data();
  const sessions = [...(data.sessions || [])];
  const currentIdx = sessions.length - 1;
  if (currentIdx < 0) throw new Error('No session found');
  sessions[currentIdx] = {
    ...sessions[currentIdx],
    dischargeDate,
    dischargeReasonId: dischargeReasonId || null,
    dischargeReasonName: dischargeReasonName || null,
  };
  await updateDoc(ref, {
    tenantId,
    status: PROGRAM_STATUS.DISCHARGED,
    sessions,
    holdReasonId: null,
    holdReasonName: null,
    holdUntilDate: null,
    updatedAt: serverTimestamp(),
    updatedBy: actorContext?.uid || null,
  });
  await writeAudit(tenantId, {
    patientId,
    programDocId,
    serviceTypeAbbreviation: data.serviceTypeAbbreviation,
    action: 'discharge',
    changes: {
      status: { old: data.status, new: PROGRAM_STATUS.DISCHARGED },
      dischargeDate: { old: null, new: dischargeDate },
      dischargeReasonName: { old: null, new: dischargeReasonName },
    },
    actorUid: actorContext?.uid,
    actorEmail: actorContext?.email,
  });
  const abbr = data.serviceTypeAbbreviation || '?';
  const sessNum = sessions[currentIdx].sessionNumber || 1;
  const reasonPart = dischargeReasonName ? ` Reason: ${dischargeReasonName}.` : '';
  await createProgramEncounter(tenantId, patientId,
    `Patient discharged from <b>${abbr}</b> program (Session ${sessNum}, effective ${dischargeDate}).${reasonPart}`,
    actorContext, dischargeDate);
}

export async function setHold(patientId, programDocId, holdReasonId, holdReasonName, holdUntilDate, actorContext) {
  const tenantId = await getEffectiveTenantId();
  if (!tenantId || !patientId || !programDocId) throw new Error('Missing context');
  const ref = tenantPatientDoc(tenantId, patientId, PROGRAMS_COL, programDocId);
  const { getDoc } = await import('firebase/firestore');
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Program not found');
  const data = snap.data();
  await updateDoc(ref, {
    tenantId,
    status: PROGRAM_STATUS.ON_HOLD,
    holdReasonId: holdReasonId || null,
    holdReasonName: holdReasonName || null,
    holdUntilDate: holdUntilDate || null,
    updatedAt: serverTimestamp(),
    updatedBy: actorContext?.uid || null,
  });
  await writeAudit(tenantId, {
    patientId,
    programDocId,
    serviceTypeAbbreviation: data.serviceTypeAbbreviation,
    action: 'hold_set',
    changes: {
      status: { old: data.status, new: PROGRAM_STATUS.ON_HOLD },
      holdReasonName: { old: null, new: holdReasonName },
      holdUntilDate: { old: null, new: holdUntilDate },
    },
    actorUid: actorContext?.uid,
    actorEmail: actorContext?.email,
  });
  const abbr = data.serviceTypeAbbreviation || '?';
  const datePart = holdUntilDate ? ` until ${holdUntilDate}` : '';
  const reasonPart = holdReasonName ? ` Reason: ${holdReasonName}.` : '';
  await createProgramEncounter(tenantId, patientId,
    `Patient's <b>${abbr}</b> program status put on hold${datePart}.${reasonPart}`,
    actorContext);
}

export async function liftHold(patientId, programDocId, actorContext) {
  const tenantId = await getEffectiveTenantId();
  if (!tenantId || !patientId || !programDocId) throw new Error('Missing context');
  const ref = tenantPatientDoc(tenantId, patientId, PROGRAMS_COL, programDocId);
  const { getDoc } = await import('firebase/firestore');
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Program not found');
  const data = snap.data();
  await updateDoc(ref, {
    tenantId,
    status: PROGRAM_STATUS.ACTIVE,
    holdReasonId: null,
    holdReasonName: null,
    holdUntilDate: null,
    updatedAt: serverTimestamp(),
    updatedBy: actorContext?.uid || null,
  });
  await writeAudit(tenantId, {
    patientId,
    programDocId,
    serviceTypeAbbreviation: data.serviceTypeAbbreviation,
    action: 'hold_lifted',
    changes: { status: { old: PROGRAM_STATUS.ON_HOLD, new: PROGRAM_STATUS.ACTIVE } },
    actorUid: actorContext?.uid,
    actorEmail: actorContext?.email,
  });
  recordDailyWin({
    uid: actorContext?.uid,
    type: 'patientActivated',
    patientId,
    eventId: `program-hold-lifted:${programDocId}:${Date.now()}`,
  });
  const abbr = data.serviceTypeAbbreviation || '?';
  await createProgramEncounter(tenantId, patientId,
    `Patient's <b>${abbr}</b> program hold removed \u2014 now Active.`,
    actorContext);
}

/**
 * Re-enroll a discharged program by appending a new session. Two modes:
 * - `{ enrollmentDate }` — activate now (actual date, today or past) → Active.
 * - `{ scheduledEnrollmentDate }` — planned date (may be future) → Pre-Service;
 *   the session's `enrollmentDate` stays null until `activateProgram`.
 *
 * @param {string} patientId
 * @param {string} programDocId
 * @param {{ enrollmentDate?: string, scheduledEnrollmentDate?: string, orderDate?: string|null, setupAlreadyBilled?: boolean }} opts
 * @param {{ uid?: string, email?: string, profile?: object }} actorContext
 */
export async function reEnrollPatient(patientId, programDocId, opts, actorContext) {
  const { enrollmentDate = null, scheduledEnrollmentDate = null, orderDate = null, setupAlreadyBilled = false } = opts || {};
  const tenantId = await getEffectiveTenantId();
  if (!tenantId || !patientId || !programDocId) throw new Error('Missing context');
  if (!enrollmentDate && !scheduledEnrollmentDate) throw new Error('An enrollment date or scheduled enrollment date is required');
  if (enrollmentDate && scheduledEnrollmentDate) throw new Error('Provide either an actual or a scheduled enrollment date, not both');
  if (enrollmentDate && enrollmentDate > todayYmd()) throw new Error('Enrollment date cannot be in the future. Use a scheduled enrollment date for planned dates.');
  const ref = tenantPatientDoc(tenantId, patientId, PROGRAMS_COL, programDocId);
  const { getDoc } = await import('firebase/firestore');
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Program not found');
  const data = snap.data();
  if (data.status !== PROGRAM_STATUS.DISCHARGED) throw new Error('Can only re-enroll discharged programs');
  const sessions = [...(data.sessions || [])];
  const nextNum = sessions.length > 0 ? (sessions[sessions.length - 1].sessionNumber || sessions.length) + 1 : 1;
  const newStatus = enrollmentDate ? PROGRAM_STATUS.ACTIVE : PROGRAM_STATUS.PRE_SERVICE;
  sessions.push({
    sessionNumber: nextNum,
    orderDate: orderDate || null,
    scheduledEnrollmentDate: scheduledEnrollmentDate || null,
    enrollmentDate: enrollmentDate || null,
    dischargeDate: null,
    dischargeReasonId: null,
    dischargeReasonName: null,
    ...(enrollmentDate && setupAlreadyBilled
      ? { setupExternallyBilled: true, setupExternallyBilledSource: 'manual' }
      : {}),
  });
  await updateDoc(ref, {
    tenantId,
    status: newStatus,
    sessions,
    holdReasonId: null,
    holdReasonName: null,
    holdUntilDate: null,
    updatedAt: serverTimestamp(),
    updatedBy: actorContext?.uid || null,
  });
  await writeAudit(tenantId, {
    patientId,
    programDocId,
    serviceTypeAbbreviation: data.serviceTypeAbbreviation,
    action: 're_enroll',
    changes: {
      status: { old: PROGRAM_STATUS.DISCHARGED, new: newStatus },
      ...(enrollmentDate ? { enrollmentDate: { old: null, new: enrollmentDate } } : {}),
      ...(scheduledEnrollmentDate ? { scheduledEnrollmentDate: { old: null, new: scheduledEnrollmentDate } } : {}),
      sessionNumber: nextNum,
    },
    actorUid: actorContext?.uid,
    actorEmail: actorContext?.email,
  });
  if (enrollmentDate) {
    recordDailyWin({
      uid: actorContext?.uid,
      type: 'patientActivated',
      patientId,
      eventId: `program-re-enroll:${programDocId}:${enrollmentDate}`,
    });
  }
  const abbr = data.serviceTypeAbbreviation || '?';
  if (enrollmentDate && setupAlreadyBilled) {
    await markSetupExternallyBilled(tenantId, patientId, enrollmentDate, nextNum);
  }
  const body = enrollmentDate
    ? `Patient re-enrolled into <b>${abbr}</b> program as Active (Session ${nextNum}, effective ${enrollmentDate}).${setupAlreadyBilled ? ' Setup billing marked as already completed in prior system.' : ''}`
    : `Patient re-enrollment into <b>${abbr}</b> program scheduled for ${scheduledEnrollmentDate} (Session ${nextNum}, Pre-Service).`;
  await createProgramEncounter(tenantId, patientId, body, actorContext, enrollmentDate || null);
}

export async function removeProgram(patientId, programDocId, actorContext) {
  const tenantId = await getEffectiveTenantId();
  if (!tenantId || !patientId || !programDocId) throw new Error('Missing context');
  const ref = tenantPatientDoc(tenantId, patientId, PROGRAMS_COL, programDocId);
  const { getDoc } = await import('firebase/firestore');
  const snap = await getDoc(ref);
  const data = snap.exists() ? snap.data() : {};
  await deleteDoc(ref);
  await writeAudit(tenantId, {
    patientId,
    programDocId,
    serviceTypeAbbreviation: data.serviceTypeAbbreviation,
    action: 'program_removed',
    actorUid: actorContext?.uid,
    actorEmail: actorContext?.email,
  });
}

/**
 * Apply a provider change atomically: terminate the current provider, add the
 * new one, discharge the patient's ACTIVE/ON_HOLD program sessions as of the
 * Effective Thru date, and (for selected programs) re-enroll under the new
 * provider as of the new effective date.
 *
 * The critical writes (patient `providerAssignments` + each affected program's
 * final state) are committed in a single Firestore `writeBatch` (all-or-nothing)
 * under the caller's own credentials, so existing security rules still apply.
 * Discharge + re-enroll are collapsed into one program write per program, so no
 * intermediate DISCHARGED state needs to persist. Audit and encounter logs are
 * written best-effort AFTER the batch commits (non-critical).
 *
 * @param {string} patientId
 * @param {{
 *   providerAssignments: Array,
 *   previousProviderAssignments?: Array,
 *   thru: string,
 *   newEffectiveDate: string,
 *   reEnrollProgramIds?: string[],
 *   orderDate?: string|null,
 * }} spec
 * @param {{ uid?: string, email?: string, profile?: object }} actorContext
 * @returns {Promise<{ affectedCount: number }>}
 */
export async function applyProviderChange(patientId, spec, actorContext) {
  const {
    providerAssignments,
    previousProviderAssignments = null,
    thru,
    newEffectiveDate,
    reEnrollProgramIds = [],
    orderDate = null,
  } = spec || {};
  const tenantId = await getEffectiveTenantId();
  if (!tenantId || !patientId) throw new Error('Missing context');
  if (!Array.isArray(providerAssignments)) throw new Error('providerAssignments is required');
  if (!thru) throw new Error('Effective thru date is required');
  if (!newEffectiveDate) throw new Error('New provider effective date is required');

  const reason = await resolveProviderChangeDischargeReason();
  const reEnrollSet = new Set(reEnrollProgramIds);

  // Fresh read of the affected (ACTIVE/ON_HOLD) programs to compute final state.
  const programsCol = tenantPatientCollection(tenantId, patientId, PROGRAMS_COL);
  const snap = await getDocs(programsCol);
  const affected = [];
  snap.forEach((d) => {
    const data = d.data();
    if (data.status === PROGRAM_STATUS.ACTIVE || data.status === PROGRAM_STATUS.ON_HOLD) {
      affected.push({ id: d.id, data });
    }
  });

  const batch = writeBatch(db);
  batch.update(tenantDoc(tenantId, 'patients', patientId), {
    providerAssignments,
    updatedAt: serverTimestamp(),
  });

  const programOps = [];
  for (const { id, data } of affected) {
    const sessions = [...(data.sessions || [])];
    const curIdx = sessions.length - 1;
    let dischargeSessionNum = null;
    if (curIdx >= 0) {
      dischargeSessionNum = sessions[curIdx].sessionNumber || curIdx + 1;
      sessions[curIdx] = {
        ...sessions[curIdx],
        dischargeDate: thru,
        dischargeReasonId: reason.id,
        dischargeReasonName: reason.name,
      };
    }
    const willReEnroll = reEnrollSet.has(id);
    let reEnrollSessionNum = null;
    if (willReEnroll) {
      const lastNum = sessions.length > 0
        ? (sessions[sessions.length - 1].sessionNumber || sessions.length)
        : 0;
      reEnrollSessionNum = lastNum + 1;
      // Documented exception to the Pre-Service invariant: provider-change
      // re-enrolls write `enrollmentDate` directly even though the new
      // effective date can be in the future. The patient is continuously in
      // service across a provider swap (already trained, has a device), so a
      // manual re-activation step would be busywork; and the census helpers
      // (sessionOpenAtStart/End) already treat a future-dated enrollment as
      // not-yet-open, so point-in-time reports remain correct on their own.
      sessions.push({
        sessionNumber: reEnrollSessionNum,
        orderDate: orderDate || null,
        scheduledEnrollmentDate: null,
        enrollmentDate: newEffectiveDate,
        dischargeDate: null,
        dischargeReasonId: null,
        dischargeReasonName: null,
      });
    }
    const newStatus = willReEnroll ? PROGRAM_STATUS.ACTIVE : PROGRAM_STATUS.DISCHARGED;
    batch.update(tenantPatientDoc(tenantId, patientId, PROGRAMS_COL, id), {
      tenantId,
      status: newStatus,
      sessions,
      holdReasonId: null,
      holdReasonName: null,
      holdUntilDate: null,
      updatedAt: serverTimestamp(),
      updatedBy: actorContext?.uid || null,
    });
    programOps.push({
      id,
      abbr: data.serviceTypeAbbreviation || '?',
      prevStatus: data.status,
      willReEnroll,
      dischargeSessionNum,
      reEnrollSessionNum,
    });
  }

  // Atomic critical write.
  await batch.commit();

  // Best-effort, non-critical logging (failures here never corrupt state).
  if (Array.isArray(previousProviderAssignments)) {
    try {
      await logPatientAuditUpdate({
        patientId,
        actorUid: actorContext?.uid,
        actorEmail: actorContext?.email,
        changes: { providerAssignments: { old: previousProviderAssignments, new: providerAssignments } },
      });
    } catch (e) {
      console.warn('Provider change patient audit failed', e);
    }
  }

  for (const op of programOps) {
    try {
      await writeAudit(tenantId, {
        patientId,
        programDocId: op.id,
        serviceTypeAbbreviation: op.abbr,
        action: 'discharge',
        changes: {
          status: { old: op.prevStatus, new: PROGRAM_STATUS.DISCHARGED },
          dischargeDate: { old: null, new: thru },
          dischargeReasonName: { old: null, new: reason.name },
        },
        actorUid: actorContext?.uid,
        actorEmail: actorContext?.email,
      });
      await createProgramEncounter(tenantId, patientId,
        `Patient discharged from <b>${op.abbr}</b> program (Session ${op.dischargeSessionNum}, effective ${thru}). Reason: ${reason.name}. [Provider change]`,
        actorContext, thru);
      if (op.willReEnroll) {
        await writeAudit(tenantId, {
          patientId,
          programDocId: op.id,
          serviceTypeAbbreviation: op.abbr,
          action: 're_enroll',
          changes: {
            status: { old: PROGRAM_STATUS.DISCHARGED, new: PROGRAM_STATUS.ACTIVE },
            enrollmentDate: { old: null, new: newEffectiveDate },
            sessionNumber: op.reEnrollSessionNum,
          },
          actorUid: actorContext?.uid,
          actorEmail: actorContext?.email,
        });
        await createProgramEncounter(tenantId, patientId,
          `Patient re-enrolled into <b>${op.abbr}</b> program as Active (Session ${op.reEnrollSessionNum}, effective ${newEffectiveDate}). [Provider change]`,
          actorContext, newEffectiveDate);
      }
    } catch (e) {
      console.warn('Provider change program log failed', e);
    }
  }

  return { affectedCount: programOps.length };
}
